import {
  createFileRoute,
  useNavigate,
  useParams,
} from '@tanstack/react-router';
import { type ReactElement, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import api from '@/api';
import { LargeArrowBackButton } from '@/common/LargeArrowBackButton';
import { TitleWithQuestion } from '@/common/TitleWithQuestion';
import { useGroupAdminAccess } from '~/hooks/useIsGroupAdmin';
import { AddPostalStudentForm } from '~/modules/students/AddPostalStudentForm';
import { FormContainer, GroupHeaderWrapper } from '~/styles/layoutElements';

export const Route = createFileRoute(
  '/_app/_dashboard/groups/$groupName/students/all-students/new',
)({
  component: RouteComponent,
});

function RouteComponent(): ReactElement | null {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { groupName } = useParams({
    from: '/_app/_dashboard/groups/$groupName/students/all-students/new',
  });

  const { isGroupAdmin, isResolved } = useGroupAdminAccess();
  const { data: group } = api.getGroupBySlug.useQuery({
    path: { group_slug: groupName },
  });

  /**
   * The leaf gate. The parent is a bare `<Outlet />` and the only group-type
   * check is the button on the index, so this URL is reachable in any group the
   * caller admins, including a congregation, where the server refuses because
   * it allows study helper groups only. A hidden button isn't a gate.
   *
   * `isResolved` matters: `getGroupBySlug` and `/users/me` are pending on first
   * paint, and a bare `!allowed` bounces the group's own admin.
   */
  const refused =
    isResolved &&
    group !== undefined &&
    !(isGroupAdmin && group.group_type === 'STUDY_HELPER_STUDY_GROUP');

  const handleBack = (): void => {
    navigate({
      to: '/groups/$groupName/students/all-students/',
      params: { groupName },
    });
  };

  useEffect(() => {
    if (refused) {
      navigate({
        to: '/groups/$groupName/students/all-students/',
        params: { groupName },
        replace: true,
      });
    }
  }, [refused, groupName, navigate]);

  if (refused) return null;

  return (
    <>
      <GroupHeaderWrapper>
        {/* No help placement, as legacy renders a bare heading. */}
        <TitleWithQuestion title={t('actions.add-new-postal-student')} />
      </GroupHeaderWrapper>
      <FormContainer>
        <LargeArrowBackButton onClick={handleBack} />
        {/* The form mounts only once the id exists: `group_id` must be
           positive, so a `0` placeholder would post to
           `/v3/groups/0/students` and get a 422 with no error code. */}
        {group !== undefined && (
          <AddPostalStudentForm groupId={group.id} onSubmitted={handleBack} />
        )}
      </FormContainer>
    </>
  );
}
